import {
  combinedSkillLevelLabel,
  looksLikeSkillLevelId,
  skillLevelMinimumLabel,
  skillLevelRank,
} from "@/utils/skillLevelDisplayUtils";

export type SkillLevelNamingState =
  | "DEFAULT"
  | "NSW"
  | "VIC"
  | "QLD"
  | "ACT";

export type SkillLevelReferenceRow = {
  skillId: string;
  rank: number;
  defaultLabel: string;
  nsw: string;
  vic: string;
  qld: string;
  act: string;
  responsibility: string;
};

export const SKILL_LEVEL_NAMING_STATE_OPTIONS: {
  value: SkillLevelNamingState;
  label: string;
}[] = [
  { value: "DEFAULT", label: "Default (universal PGY labels)" },
  { value: "NSW", label: "New South Wales (NSW)" },
  { value: "VIC", label: "Victoria (VIC)" },
  { value: "QLD", label: "Queensland (QLD)" },
  { value: "ACT", label: "ACT & other states / territories" },
];

/** Mirrors backend SkillLevelStateNamingCatalog — used offline / before API loads. */
export const SKILL_LEVEL_REFERENCE_ROWS: SkillLevelReferenceRow[] = [
  {
    skillId: "sk-1",
    rank: 1,
    defaultLabel: combinedSkillLevelLabel("sk-1"),
    nsw: "Intern",
    vic: "Intern",
    qld: "Intern",
    act: "Intern / PGY1",
    responsibility: "PGY 1: Supervised practice; hospital-dependent.",
  },
  {
    skillId: "sk-2",
    rank: 2,
    defaultLabel: combinedSkillLevelLabel("sk-2"),
    nsw: "Resident Medical Officer (RMO) – Year 1",
    vic: "Hospital Medical Officer – Year 2 (HMO2)",
    qld: "Junior House Officer (JHO)",
    act: "Resident Medical Officer (RMO) / PGY2",
    responsibility: "PGY 2: General ward coverage; basic rotations.",
  },
  {
    skillId: "sk-3",
    rank: 3,
    defaultLabel: combinedSkillLevelLabel("sk-3"),
    nsw: "Resident Medical Officer (RMO) – Year 2+, Senior RMO (SRMO)",
    vic: "Hospital Medical Officer – Year 3+ (HMO3 / HMO4)",
    qld: "Senior House Officer (SHO)",
    act: "Senior RMO / PGY3 / PGY4",
    responsibility: "PGY 3–4: Experienced resident; targeted specialty streams.",
  },
  {
    skillId: "sk-4",
    rank: 4,
    defaultLabel: combinedSkillLevelLabel("sk-4"),
    nsw: "Registrar, Unaccredited Registrar",
    vic: "Registrar, Unaccredited Registrar",
    qld: "Principal House Officer (PHO), Registrar",
    act: "Registrar, Registrar (Non-Training)",
    responsibility: "PGY 4+ (Trainee): Admitting rights; managing specialty rosters.",
  },
  {
    skillId: "sk-5",
    rank: 5,
    defaultLabel: combinedSkillLevelLabel("sk-5"),
    nsw: "Provisional Fellow, Fellow",
    vic: "Senior Registrar, Fellow",
    qld: "Fellow",
    act: "Fellow, Senior Registrar",
    responsibility: "PGY 6+ (Senior Trainee): Transitioning to independent practice.",
  },
  {
    skillId: "sk-6",
    rank: 6,
    defaultLabel: combinedSkillLevelLabel("sk-6"),
    nsw: "Staff Specialist, Visiting Medical Officer (VMO), Career Medical Officer (CMO)",
    vic: "Consultant, Specialist",
    qld: "Senior Medical Officer (SMO), Visiting Medical Officer (VMO)",
    act: "Consultant, Senior Medical Officer (SMO)",
    responsibility:
      "Post-Fellowship: Fully independent practitioner / Senior clinician.",
  },
];

const SHORT_BY_STATE: Record<
  Exclude<SkillLevelNamingState, "DEFAULT">,
  Record<string, string>
> = {
  NSW: {
    "sk-1": "Intern",
    "sk-2": "RMO – Year 1",
    "sk-3": "RMO – Year 2+ / SRMO",
    "sk-4": "Registrar",
    "sk-5": "Fellow",
    "sk-6": "Staff Specialist / VMO / CMO",
  },
  VIC: {
    "sk-1": "Intern",
    "sk-2": "HMO2",
    "sk-3": "HMO3 / HMO4",
    "sk-4": "Registrar",
    "sk-5": "Senior Registrar / Fellow",
    "sk-6": "Consultant / Specialist",
  },
  QLD: {
    "sk-1": "Intern",
    "sk-2": "JHO",
    "sk-3": "SHO",
    "sk-4": "PHO / Registrar",
    "sk-5": "Fellow",
    "sk-6": "SMO / VMO",
  },
  ACT: {
    "sk-1": "Intern / PGY1",
    "sk-2": "RMO / PGY2",
    "sk-3": "Senior RMO / PGY3 / PGY4",
    "sk-4": "Registrar",
    "sk-5": "Fellow / Senior Registrar",
    "sk-6": "Consultant / SMO",
  },
};

export function normalizeSkillLevelNamingState(
  raw?: string | null,
): SkillLevelNamingState {
  const value = String(raw || "DEFAULT")
    .trim()
    .toUpperCase();
  if (value === "OTHER" || value === "WA" || value === "SA" || value === "TAS" || value === "NT") {
    return "ACT";
  }
  if (
    value === "NSW" ||
    value === "VIC" ||
    value === "QLD" ||
    value === "ACT" ||
    value === "DEFAULT"
  ) {
    return value;
  }
  return "DEFAULT";
}

export function stateShortSkillLabel(
  skillId: string | null | undefined,
  state: SkillLevelNamingState,
): string | null {
  if (!skillId || state === "DEFAULT") return null;
  const id = skillId.toLowerCase();
  return SHORT_BY_STATE[state]?.[id] || null;
}

/** Infer sk-* from a stored id or any known default/state display label. */
export function inferSkillIdFromAnyLabel(
  value?: string | null,
): string | null {
  const raw = String(value || "").trim();
  if (!raw) return null;
  if (looksLikeSkillLevelId(raw)) {
    return raw.toLowerCase();
  }
  const withoutSuffix = raw.replace(/\s+and higher\s*$/i, "").trim();
  const lower = withoutSuffix.toLowerCase();

  for (const row of SKILL_LEVEL_REFERENCE_ROWS) {
    const candidates = [
      row.defaultLabel,
      combinedSkillLevelLabel(row.skillId),
      skillLevelMinimumLabel(row.skillId),
      row.nsw,
      row.vic,
      row.qld,
      row.act,
    ];
    if (
      candidates.some(
        (c) =>
          c.toLowerCase() === lower ||
          lower.startsWith(c.toLowerCase()) ||
          c.toLowerCase().startsWith(lower),
      )
    ) {
      return row.skillId;
    }
  }
  if (/staff specialist|career medical officer/.test(lower)) return "sk-6";
  if (/senior registrar|provisional fellow|\bfellow\b/.test(lower)) return "sk-5";
  return null;
}

export function resolveSkillLevelDisplayLabel(args: {
  skillId?: string | null;
  canonicalName?: string | null;
  namingState?: SkillLevelNamingState;
  minimum?: boolean;
}): string {
  const state = args.namingState || "DEFAULT";
  const stateLabel = stateShortSkillLabel(args.skillId, state);
  const base =
    stateLabel ||
    combinedSkillLevelLabel(args.skillId, args.canonicalName) ||
    String(args.canonicalName || args.skillId || "").trim();

  if (!base) return "";
  if (!args.minimum) return base;
  if (base.toLowerCase().includes("and higher")) return base;
  if (skillLevelRank(args.skillId) >= 6) return base;
  return `${base} and higher`;
}

/** Hospital-facing minimum skill phrase. Uses the active state names, not a leftover Consultant label. */
export function skillMinimumPhrase(
  raw?: string | null,
  namingState?: SkillLevelNamingState,
): string {
  const id = inferSkillIdFromAnyLabel(raw);
  if (namingState) {
    return resolveSkillLevelDisplayLabel({
      skillId: id,
      canonicalName: raw,
      namingState,
      minimum: true,
    });
  }
  const source = String(raw || "").trim();
  if (
    source &&
    !looksLikeSkillLevelId(source) &&
    !/^consultant/i.test(source) &&
    !/^smo\s*\/\s*vmo/i.test(source)
  ) {
    if (/and higher/i.test(source)) return source;
    if (id && skillLevelRank(id) >= 6) return source;
    return `${source.replace(/\s+and higher\s*$/i, "")} and higher`;
  }
  return resolveSkillLevelDisplayLabel({
    skillId: id,
    canonicalName: raw,
    namingState: "DEFAULT",
    minimum: true,
  });
}

export function buildSkillOptionsFromCatalog(
  rows: Array<{ sysId: string; sysName?: string }>,
  namingState: SkillLevelNamingState,
) {
  return [...rows]
    .sort((a, b) => skillLevelRank(a.sysId) - skillLevelRank(b.sysId))
    .map((row) => ({
      value: row.sysId,
      label: resolveSkillLevelDisplayLabel({
        skillId: row.sysId,
        canonicalName: row.sysName,
        namingState,
        minimum: true,
      }),
    }));
}
